import { createContext, ReactNode, useLayoutEffect, useState } from "react";

import {
  useSidebarMenuItemsActive,
  useSidebarMenuItemsVisible
} from "./hooks/useSidebarMenuItems";
import { SidebarMenuItem, SidebarMenuItemState } from "./types";

type Context = {
  visibleItems: SidebarMenuItemState;
  activeItems: SidebarMenuItemState;
  collapsed: boolean;
  toggleCollapseItems: (activeItem: SidebarMenuItem) => void;
};

export const SidebarContext = createContext<Context>({
  visibleItems: {
    overview: true,
    examPrep: true,
    assignments: true,
    askAmelia: true
  },
  activeItems: {
    overview: true,
    examPrep: false,
    assignments: false,
    askAmelia: false
  },
  collapsed: false,
  toggleCollapseItems: () => {}
});

export default function SidebarContextProvider({
  children
}: {
  children: ReactNode;
}) {
  const activeItems = useSidebarMenuItemsActive();
  const initialVisibleItems = useSidebarMenuItemsVisible();
  const [visibleItems, setVisibleItems] =
    useState<SidebarMenuItemState>(initialVisibleItems);
  const [collapsed, setCollapsed] = useState(false);

  useLayoutEffect(
    () => setVisibleItems(initialVisibleItems),
    [initialVisibleItems]
  );

  const toggleCollapseItems = (activeItem: SidebarMenuItem) => {
    if (collapsed) {
      setCollapsed(false);
      setVisibleItems(initialVisibleItems);
    } else {
      setCollapsed(true);
      setVisibleItems(visibleItems => {
        return (Object.keys(visibleItems) as SidebarMenuItem[]).reduce(
          (acc, visibleItem) => {
            if (visibleItem === activeItem) acc[visibleItem] = true;
            else acc[visibleItem] = false;
            return acc;
          },
          {} as SidebarMenuItemState
        );
      });
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        visibleItems,
        activeItems,
        collapsed,
        toggleCollapseItems
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}
